import { useState } from 'react'
import type { Locale, NodeType } from '../data/schema'
import { copy } from '../i18n'
import { TypeIcon } from './TypeIcon'

const types: NodeType[] = ['deity', 'hero', 'mortal', 'creature', 'place', 'event', 'artifact']

export type NodeLegendBehavior = 'filter' | 'highlight' | 'related-filter'

interface NodeLegendProps {
  locale: Locale
  active: Set<NodeType>
  onToggle: (type: NodeType) => void
  behavior?: NodeLegendBehavior
  highlightedType?: NodeType
  onHighlight?: (type?: NodeType) => void
}

export function NodeLegend({ locale, active, onToggle, behavior = 'filter', highlightedType, onHighlight }: NodeLegendProps) {
  const [expanded, setExpanded] = useState(false)
  const t = copy[locale]
  const isHighlight = behavior === 'highlight' || behavior === 'related-filter'
  const controlLabel = behavior === 'related-filter'
    ? locale === 'en' ? 'Filter related items' : '篩選相關項目'
    : isHighlight
      ? locale === 'en' ? 'Highlight item type' : '突出項目類型'
      : t.shapeLegend

  return (
    <section className={`node-legend ${expanded ? 'expanded' : ''}${isHighlight ? ` type-highlight-control${highlightedType ? ' has-highlight' : ''}` : ''}`} aria-label={controlLabel}>
      <button className="legend-toggle" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
        <span className="legend-constellation"><i /><i /><i /></span>
        <span>{controlLabel}</span>
      </button>
      <div className="legend-items">
        {isHighlight && <button
          className={`legend-all${highlightedType ? '' : ' active'}`}
          onClick={() => onHighlight?.(undefined)}
          aria-pressed={!highlightedType}
        >
          <span className="legend-constellation"><i /><i /><i /></span>
          <span>{locale === 'en' ? 'All' : '全部'}</span>
        </button>}
        {types.map((type) => (
          <button
            key={type}
            className={(isHighlight ? highlightedType === type : active.has(type)) ? 'active' : ''}
            onClick={() => isHighlight ? onHighlight?.(highlightedType === type ? undefined : type) : onToggle(type)}
            aria-pressed={isHighlight ? highlightedType === type : active.has(type)}
            aria-label={isHighlight
              ? behavior === 'related-filter'
                ? locale === 'en' ? `Show related ${t.nodeTypes[type]} items` : `顯示相關${t.nodeTypes[type]}`
                : locale === 'en' ? `Highlight ${t.nodeTypes[type]} items` : `突出${t.nodeTypes[type]}項目`
              : undefined}
          >
            <span className={`type-sigil ${type}`}><TypeIcon type={type} /></span>
            <span>{t.nodeTypes[type]}</span>
          </button>
        ))}
      </div>
    </section>
  )
}
